import { cn } from "@/lib/util";
import { Clock, CheckCircle2, AlertCircle, Mail, Loader2 } from "lucide-react";

/**
 * Vertical timeline of rule firings. Purely presentational — parent owns state.
 */
export function FiringTimeline({ items, selectedId, onSelect }) {
  if (items === null) {
    return (
      <div className="p-6 flex items-center gap-2 text-muted-foreground text-xs font-mono">
        <Loader2 className="w-3 h-3 animate-spin" /> Cargando cronología...
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="p-8 text-center text-xs text-muted-foreground border border-dashed border-border" data-testid="firings-empty">
        <Clock className="w-8 h-8 mx-auto mb-2 opacity-40" />
        Aún no hay disparos registrados para esta regla.
      </div>
    );
  }
  return (
    <ol className="relative border-l-2 border-border ml-3">
      {items.map((f) => (
        <FiringTimelineItem key={f.id} firing={f} active={selectedId === f.id} onClick={() => onSelect(f)} />
      ))}
    </ol>
  );
}

const softReasons = new Set(["no_recipients", "email_alerts_disabled", "smtp_not_configured"]);

function dotColor(f) {
  if (f.dispatched) return "bg-sev-low border-sev-low";
  if (softReasons.has(f.reason)) return "bg-sev-medium border-sev-medium";
  return "bg-sev-critical border-sev-critical";
}

function FiringTimelineItem({ firing, active, onClick }) {
  return (
    <li
      onClick={onClick}
      className={cn(
        "ml-4 mb-2 py-3 pr-3 pl-4 border cursor-pointer transition-colors relative",
        active ? "border-primary bg-primary/5" : "border-border bg-background/40 hover:bg-muted/30"
      )}
      data-testid={`firing-${firing.id}`}
    >
      <span className={cn("absolute -left-[9px] top-4 w-3 h-3 rounded-full border-2", dotColor(firing))} />
      <div className="flex items-center justify-between gap-2">
        <div className="font-mono text-[11px] text-muted-foreground">{new Date(firing.fired_at).toLocaleString()}</div>
        {firing.dispatched ? (
          <span className="font-mono text-[10px] tracking-widest uppercase text-sev-low flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> enviado
          </span>
        ) : (
          <span className="font-mono text-[10px] tracking-widest uppercase text-sev-medium flex items-center gap-1">
            <AlertCircle className="w-3 h-3" /> {firing.reason || "no enviado"}
          </span>
        )}
      </div>
      <div className="text-sm mt-1 truncate">{firing.subject}</div>
      <div className="font-mono text-[10px] text-muted-foreground mt-1 flex items-center gap-1 truncate">
        <Mail className="w-3 h-3" />
        {firing.recipients?.join(", ") || <span className="italic">sin destinatarios</span>}
      </div>
    </li>
  );
}
